import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../config";
import { uploadFile as uploadToMediaService } from "../uploadMedia";
import { mediaKind, withinMediaLimit } from "../mediaLimits";
import { getEcho } from "../echo";
import { ensureNotificationPermission } from "../chatNotify";
import InboxPane from "./InboxPane";
import ThreadPane from "./ThreadPane";
import DiscoverPane from "./DiscoverPane";
import DetailsPane from "./DetailsPane";
import Lightbox from "./Lightbox";
import {
  Avatar,
  isImageUrl,
  previewLabel,
  fullTime,
  brandGradient,
  CloseIcon,
  ForwardIcon,
  CheckIcon,
  TrashIcon,
} from "./ui";

/** Sort helper that keeps optimistic (string-id) messages pinned to the end. */
const idRank = (id) => (typeof id === "number" ? id : Number.MAX_SAFE_INTEGER);
const sortMsgs = (arr) => arr.slice().sort((a, b) => idRank(a.id) - idRank(b.id));

/** Tailwind `sm` breakpoint, as a hook — drives phone-only behaviour. */
function useCompact() {
  const [compact, setCompact] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = (e) => setCompact(e.matches);
    setCompact(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return compact;
}

const lsGet = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
};
const lsSet = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* quota / private mode */
  }
};

/**
 * Member chat. Renders either as a full-page card (`embedded` — the Members Chat
 * nav section) or as a centred overlay dialog (opened from a message
 * notification). Owns every piece of chat state; the panes are presentational.
 */
export default function ChatShell({
  token,
  member,
  primary = "#0274F7",
  onClose,
  onUnreadChange,
  startWith = null,
  embedded = false,
}) {
  const memberToken = member?.auth_token;
  const myId = member?.community_member_id;
  const compact = useCompact();

  /* ------------------------------------------------------------- state */
  const [conversations, setConversations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [previewContact, setPreviewContact] = useState(null);
  // New-message scope: you chat within a space you share — a GROUP you joined or
  // a COURSE you have (mirrors the Members page, which offers the same two
  // lists). The two are mutually exclusive; contacts stay empty until one is
  // picked. `contactMode` decides which list the picker's dropdown shows.
  const [contactMode, setContactMode] = useState("courses"); // "courses" | "groups"
  const [contactGroup, setContactGroup] = useState(""); // group_token
  const [contactGroups, setContactGroups] = useState([]);
  const [contactCourse, setContactCourse] = useState(""); // funnel_page_id as string
  const [contactCourses, setContactCourses] = useState([]);
  const [filter, setFilter] = useState("all");

  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingAttach, setPendingAttach] = useState(null); // { file, url, kind, name } staged before send
  const [otherTyping, setOtherTyping] = useState(false);
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [reactFor, setReactFor] = useState(null);
  const [communityId, setCommunityId] = useState(null);
  const [convoSearch, setConvoSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [viewArchived, setViewArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [viewBlocked, setViewBlocked] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // { cid, name } awaiting delete confirm

  // New in the redesign
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images, index }
  const [forwardMsgs, setForwardMsgs] = useState(null); // message[] awaiting a target
  const [forwardPicks, setForwardPicks] = useState(() => new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulkDel, setConfirmBulkDel] = useState(null); // messages awaiting the bulk-delete confirm
  const [bulkBusy, setBulkBusy] = useState(null); // { done, total } while the run is in flight
  const [infoMsg, setInfoMsg] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [unreadAnchorId, setUnreadAnchorId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [wallpaper, setWallpaper] = useState("aura");

  const scrollRef = useRef(null);
  const searchTimer = useRef(null);
  const searchBoxRef = useRef(null);
  const activeIdRef = useRef(null);
  const initedRef = useRef(false);
  const lastWhisperRef = useRef(0);
  const typingTimer = useRef(null);
  const stickRef = useRef(true);
  const draftsRef = useRef({});
  const tmpSeq = useRef(0);

  activeIdRef.current = active?.conversation_id ?? null;

  const api = useCallback(
    (path, body) =>
      axios.post(`${config.COMMUNITY_API_URL}/${path}`, { community_token: token, member_token: memberToken, ...body }),
    [token, memberToken]
  );

  /* ------------------------------------------------------- data loaders */
  const loadConversations = useCallback(async () => {
    try {
      const res = await api("membership-chat-conversations", viewArchived || filter === "archived" ? { archived: 1 } : {});
      if (res.data?.error === 0) {
        setConversations(res.data.conversations || []);
        if (res.data.community_id) setCommunityId(res.data.community_id);
        setArchivedCount(res.data.archived_count || 0);
        onUnreadChange?.(res.data.total_unread || 0);
      }
    } catch {
      /* ignore — polling retries */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, viewArchived, filter]);

  const loadFavorites = useCallback(async () => {
    try {
      const res = await api("membership-chat-favorites", {});
      if (res.data?.error === 0) setFavorites(res.data.favorites || []);
    } catch {
      /* ignore */
    }
  }, [api]);

  const loadBlocked = useCallback(async () => {
    try {
      const res = await api("membership-chat-blocked", {});
      if (res.data?.error === 0) setBlocked(res.data.blocked || []);
    } catch {
      /* ignore */
    }
  }, [api]);

  const loadContacts = useCallback(
    async (q = "", group = "", course = "") => {
      try {
        const res = await api("membership-chat-contacts", {
          q,
          group_token: group || undefined,
          course_page_id: course || undefined,
        });
        if (res.data?.error === 0) {
          setContacts(res.data.contacts || []);
          // Both scope lists come back on every call — keep them fresh so the
          // picker's dropdown is always populated whichever mode it's in.
          if (Array.isArray(res.data.groups)) setContactGroups(res.data.groups);
          if (Array.isArray(res.data.courses)) setContactCourses(res.data.courses);
        }
      } catch {
        /* ignore */
      }
    },
    [api]
  );

  /* ------------------------------------------------------------ actions */
  const unblockMember = async (mtoken) => {
    try {
      const res = await api("membership-chat-block", { target_member_token: mtoken, action: "unblock" });
      if (res.data?.error === 0) {
        toast.info("Member unblocked.");
        loadBlocked();
        loadConversations();
      }
    } catch {
      /* ignore */
    }
  };

  const onSearchChange = (v) => {
    setConvoSearch(v);
    clearTimeout(searchTimer.current);
    if (v.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api("membership-chat-search", { q: v.trim() });
        if (res.data?.error === 0) setSearchResults(res.data.results || []);
      } catch {
        /* ignore */
      }
    }, 300);
  };

  const convoAction = async (cid, action, name) => {
    setRowMenu(null);
    // Deleting a chat is destructive and clears its history for you — confirm
    // first (WhatsApp-style), then run doDeleteConvo().
    if (action === "delete") {
      setConfirmDel({ cid, name: name || "this chat" });
      return;
    }
    try {
      await api("membership-chat-conversation-action", { conversation_id: cid, action });
      loadConversations();
    } catch {
      /* ignore */
    }
  };

  const doDeleteConvo = async () => {
    const cid = confirmDel?.cid;
    setConfirmDel(null);
    if (!cid) return;
    try {
      await api("membership-chat-conversation-action", { conversation_id: cid, action: "delete" });
      // Clear the open thread if it's the one being deleted — the messages state
      // otherwise lingers and would reappear (the bug this fixes).
      if (active?.conversation_id === cid) {
        setActive(null);
        setMessages([]);
        setDetailsOpen(false);
      }
      loadConversations();
      toast.success("Chat deleted.");
    } catch {
      toast.error("Could not delete the chat.");
    }
  };

  const openConversation = useCallback(
    async (convo) => {
      setActive(convo);
      setShowContacts(false);
      setPreviewContact(null);
      setEditing(null);
      setMenuOpen(false);
      setFindOpen(false);
      setSelectMode(false);
      setSelectedIds(new Set());
      setLoadingMsgs(true);
      setMessages([]);
      setText(draftsRef.current[convo.conversation_id] || "");
      setWallpaper(lsGet(`chatwall:${token}:${convo.conversation_id}`, "aura"));
      stickRef.current = true;
      try {
        const res = await api("membership-chat-messages", { conversation_id: convo.conversation_id });
        if (res.data?.error === 0) {
          const list = res.data.messages || [];
          setMessages(list);
          setHasMore(!!res.data.has_more);
          // First unread from the other side → the "Unread messages" divider.
          const n = convo.unread || 0;
          if (n > 0) {
            const theirs = list.filter((m) => !m.is_me);
            const anchor = theirs[Math.max(0, theirs.length - n)];
            setUnreadAnchorId(anchor?.id ?? null);
          } else {
            setUnreadAnchorId(null);
          }
        }
        setConversations((list) => {
          const next = list.map((c) => (c.conversation_id === convo.conversation_id ? { ...c, unread: 0 } : c));
          onUnreadChange?.(next.reduce((s, c) => s + (c.unread || 0), 0));
          return next;
        });
      } catch {
        toast.error("Could not load messages.");
      } finally {
        setLoadingMsgs(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, token]
  );

  const loadOlder = async () => {
    if (!active || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const first = messages.find((m) => typeof m.id === "number");
      const res = await api("membership-chat-messages", {
        conversation_id: active.conversation_id,
        before_id: first?.id,
      });
      if (res.data?.error === 0) {
        const older = res.data.messages || [];
        setMessages((m) => [...older, ...m]);
        setHasMore(!!res.data.has_more);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  };

  const startChatWith = useCallback(
    async (contact) => {
      try {
        const res = await api("membership-chat-open", { target_member_token: contact.member_token });
        if (res.data?.error === 0) {
          const convo = res.data.conversation;
          setConversations((list) =>
            list.some((c) => c.conversation_id === convo.conversation_id)
              ? list
              : [{ ...convo, last_message: null, last_message_at: null, unread: 0 }, ...list]
          );
          openConversation(convo);
        } else {
          toast.error(res.data?.msg || "Could not start chat.");
        }
      } catch (e) {
        toast.error(e.response?.data?.msg || "Could not start chat.");
      }
    },
    [api, openConversation]
  );

  /* ------------------------------------------------------------ sending */
  // Optimistic: the bubble appears immediately as "sending", then flips to the
  // server copy, or to "failed" with a retry affordance.
  const postMessage = useCallback(
    async (body, { replyId = 0, tmpId = null, conversationId = null } = {}) => {
      const cid = conversationId || active?.conversation_id;
      if (!body || !cid) return false;
      // Forwards target another conversation — never touch the open thread's
      // message list for those, only the inbox row.
      const inThread = cid === active?.conversation_id;
      const tmp = tmpId || `tmp-${++tmpSeq.current}`;
      if (inThread) stickRef.current = true;

      if (inThread) {
        if (!tmpId) {
          setMessages((m) => [
            ...m,
            {
              id: tmp,
              body,
              is_me: true,
              sender_id: myId,
              created_at: new Date().toISOString(),
              reactions: [],
              reply_to: replyId ? { id: replyId, sender_id: replyTo?.is_me ? myId : null, preview: replyTo?.preview } : null,
              status: "sending",
            },
          ]);
        } else {
          setMessages((m) => m.map((x) => (x.id === tmpId ? { ...x, status: "sending" } : x)));
        }
      }

      setSending(true);
      try {
        const res = await api("membership-chat-send", { conversation_id: cid, body, reply_to_id: replyId });
        if (res.data?.error === 0) {
          const msg = res.data.message;
          if (inThread) {
            setReplyTo(null);
            setMessages((m) => sortMsgs(m.filter((x) => x.id !== tmp && x.id !== msg.id).concat(msg)));
          }
          setConversations((list) => {
            const exists = list.find((c) => c.conversation_id === cid);
            if (!exists) return list; // a brand-new thread; the next reload adds it
            const updated = { ...exists, last_message: body, last_message_at: msg.created_at, unread: 0 };
            return [updated, ...list.filter((c) => c.conversation_id !== cid)];
          });
          return true;
        }
        toast.error(res.data?.msg || "Could not send.");
      } catch (e) {
        toast.error(e.response?.data?.msg || "Could not send.");
      } finally {
        setSending(false);
      }
      if (inThread) setMessages((m) => m.map((x) => (x.id === tmp ? { ...x, status: "failed" } : x)));
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, active, myId, replyTo]
  );

  const saveEdit = async () => {
    const body = text.trim();
    if (!body || !editing) return;
    const { id } = editing;
    try {
      const res = await api("membership-chat-edit", { conversation_id: active.conversation_id, message_id: id, body });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === id ? { ...x, body, edited: true } : x)));
        setEditing(null);
        setText("");
      } else {
        toast.error(res.data?.msg || "Could not edit.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not edit.");
    }
  };

  const send = async () => {
    if (editing) return saveEdit();
    const body = text.trim();

    // Staged attachment → upload, then send the media (and any caption).
    if (pendingAttach) {
      const att = pendingAttach;
      setPendingAttach(null);
      setText("");
      if (active) {
        delete draftsRef.current[active.conversation_id];
        lsSet(`chatdrafts:${token}`, JSON.stringify(draftsRef.current));
      }
      setUploading(true);
      try {
        const url = await uploadFile(att.file);
        if (url) {
          // ONE message: the attachment URL, with the caption on the line below
          // it ("<url>\n<caption>"). The renderer shows both inside a single
          // bubble (WhatsApp-style) instead of two separate messages.
          await postMessage(body ? `${url}\n${body}` : url);
        } else {
          toast.error("Upload failed.");
        }
      } catch (e) {
        // The media service throws with a message worth showing (e.g. an
        // unsupported type), so prefer that over a generic failure.
        toast.error(e.response?.data?.msg || e.message || "Upload failed.");
      } finally {
        setUploading(false);
        if (att.url) {
          try {
            URL.revokeObjectURL(att.url);
          } catch {
            /* ignore */
          }
        }
      }
      return;
    }

    if (!body) return;
    setText("");
    // Clear the saved draft in BOTH memory and localStorage — otherwise the sent
    // text (e.g. a URL) is restored into the input on the next open/reload.
    if (active) {
      delete draftsRef.current[active.conversation_id];
      lsSet(`chatdrafts:${token}`, JSON.stringify(draftsRef.current));
    }
    await postMessage(body, { replyId: replyTo?.id || 0 });
  };

  const retryMessage = (m) => postMessage(m.body, { tmpId: m.id });

  // Upload a file and return its hosted URL (or null). Does NOT send.
  //
  // Everything — photos, video, voice notes and documents — goes to the shared
  // media service, the same place feed posts upload to; the membership API's own
  // /storage/chat write is what fails in production. uploadMedia.js picks the
  // field the service wants for each kind (images[] for photos, singular
  // video / audio / file for the rest). The message body still just carries the
  // URL, so nothing downstream changes: the
  // renderers sniff the extension, which the service preserves. It does rename
  // the file, so document cards show the stored name rather than the one the
  // sender picked (fileMeta in ui.jsx reads the name off the URL).
  const uploadFile = (file) =>
    uploadToMediaService(file, { communityToken: token, memberId: myId });

  // Stage a picked/pasted/dropped file in the composer (preview + optional
  // caption) instead of firing it off immediately — the user reviews, then sends.
  const stageFile = (file) => {
    if (!file || !active) return;
    const kind = mediaKind(file);
    // Refuse an oversized file here rather than after the upload — nothing is
    // staged, so the composer never shows an attachment that can't be sent.
    if (!withinMediaLimit(file, kind)) return;
    let url = "";
    try {
      url = URL.createObjectURL(file);
    } catch {
      /* ignore */
    }
    setPendingAttach((prev) => {
      if (prev?.url) {
        try {
          URL.revokeObjectURL(prev.url);
        } catch {
          /* ignore */
        }
      }
      return { file, url, kind, name: file.name };
    });
  };

  const clearPending = () => {
    setPendingAttach((prev) => {
      if (prev?.url) {
        try {
          URL.revokeObjectURL(prev.url);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
  };

  /**
   * Delete one message. Returns true when the row is gone (or tombstoned), so
   * the bulk path can count what actually happened instead of assuming.
   * `quiet` suppresses the single-message toast — bulk reports once at the end.
   */
  const deleteMsg = async (id, { quiet = false } = {}) => {
    if (typeof id !== "number") {
      setMessages((m) => m.filter((x) => x.id !== id)); // drop a failed optimistic bubble
      return true;
    }
    try {
      const res = await api("membership-chat-delete", { conversation_id: active.conversation_id, message_id: id });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === id ? { ...x, deleted: true, body: null, reactions: [] } : x)));
        return true;
      }
      if (!quiet) toast.error("Could not delete.");
      return false;
    } catch {
      if (!quiet) toast.error("Could not delete.");
      return false;
    }
  };

  /* Which of the selected messages this member is actually allowed to delete.
     The endpoint deletes YOUR OWN messages only (MembershipChatController@
     deleteMessage answers 403 for anyone else's), and a tombstone cannot be
     deleted twice — so the selection is filtered here rather than fired at the
     server and silently refused, which is what made bulk delete look broken. */
  const deletableSelected = useMemo(
    () => messages.filter((m) => selectedIds.has(m.id) && m.is_me && !m.deleted),
    [messages, selectedIds]
  );

  /* Sequential, not a parallel forEach: the old code fired one unawaited
     request per message and never looked at the answers, so a partial failure
     was invisible and the thread could settle out of sync with the server.
     Small N, so the extra latency is not worth a burst of parallel writes. */
  const runBulkDelete = async () => {
    const list = confirmBulkDel || [];
    setConfirmBulkDel(null);
    if (!list.length) return;
    setBulkBusy({ done: 0, total: list.length });
    let ok = 0;
    for (const m of list) {
      // eslint-disable-next-line no-await-in-loop
      if (await deleteMsg(m.id, { quiet: true })) ok += 1;
      setBulkBusy((b) => (b ? { ...b, done: b.done + 1 } : b));
    }
    setBulkBusy(null);
    setSelectMode(false);
    setSelectedIds(new Set());
    const noun = ok === 1 ? "Message" : `${ok} messages`;
    if (ok === list.length) toast.success(`${noun} deleted.`);
    else if (ok) toast.warn(`${ok} of ${list.length} deleted — the rest could not be removed.`);
    else toast.error("Could not delete those messages.");
  };

  const toggleReaction = async (msg, emoji) => {
    setReactFor(null);
    if (typeof msg.id !== "number") return;
    try {
      const res = await api("membership-chat-react", {
        conversation_id: active.conversation_id,
        message_id: msg.id,
        emoji,
      });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, reactions: res.data.reactions || [] } : x)));
      }
    } catch {
      /* ignore */
    }
  };

  /* -------------------------------------------------- thread-level menu */
  const activeConvo = active ? conversations.find((c) => c.conversation_id === active.conversation_id) : null;
  const isMuted = !!activeConvo?.muted;
  const isFavorite = active ? favorites.some((f) => f.member_token === active.member_token) : false;
  const otherId = active?.other_id ?? activeConvo?.other_id;
  const otherOnline = otherId ? onlineIds.has(Number(otherId)) : false;

  const toggleMute = async () => {
    if (!active) return;
    const action = isMuted ? "unmute" : "mute";
    setMenuOpen(false);
    try {
      const res = await api("membership-chat-mute", { conversation_id: active.conversation_id, action });
      if (res.data?.error === 0) {
        setConversations((list) =>
          list.map((c) => (c.conversation_id === active.conversation_id ? { ...c, muted: res.data.muted ? 1 : 0 } : c))
        );
        toast.info(res.data.muted ? "Conversation muted." : "Conversation unmuted.");
      }
    } catch {
      /* ignore */
    }
  };

  const toggleFavorite = async () => {
    if (!active?.member_token) return;
    setMenuOpen(false);
    try {
      const res = await api("membership-chat-favorite", {
        target_member_token: active.member_token,
        action: isFavorite ? "remove" : "add",
      });
      if (res.data?.error === 0) {
        loadFavorites();
        toast.info(res.data.favorite ? "Added to favourites." : "Removed from favourites.");
      }
    } catch {
      /* ignore */
    }
  };

  // Favourite/unfavourite a conversation straight from its inbox-row menu
  // (keyed by member_token, so it works for any row, not just the open thread).
  const toggleFavoriteFor = async (convo) => {
    setRowMenu(null);
    if (!convo?.member_token) return;
    const already = favorites.some((f) => f.member_token === convo.member_token);
    try {
      const res = await api("membership-chat-favorite", {
        target_member_token: convo.member_token,
        action: already ? "remove" : "add",
      });
      if (res.data?.error === 0) {
        loadFavorites();
        toast.info(res.data.favorite ? "Added to favourites." : "Removed from favourites.");
      }
    } catch {
      /* ignore */
    }
  };

  const blockMember = async () => {
    if (!active?.member_token) return;
    setMenuOpen(false);
    setDetailsOpen(false);
    try {
      const res = await api("membership-chat-block", { target_member_token: active.member_token, action: "block" });
      if (res.data?.error === 0) {
        toast.info(`${active.name} blocked.`);
        setActive(null);
        loadConversations();
        loadBlocked();
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not block.");
    }
  };

  const reportMember = async (reason) => {
    if (!active?.member_token) return;
    try {
      const res = await api("membership-chat-report", {
        target_member_token: active.member_token,
        conversation_id: active.conversation_id,
        reason,
      });
      toast.info(res.data?.msg || "Report submitted. Thank you.");
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not submit the report.");
    }
  };

  /* --------------------------------------------------------- forwarding */
  const doForward = async () => {
    const msgs = forwardMsgs || [];
    const picks = Array.from(forwardPicks);
    setForwardMsgs(null);
    setForwardPicks(new Set());
    if (!msgs.length || !picks.length) return;
    let sent = 0;
    for (const key of picks) {
      let cid = null;
      const convo = conversations.find((c) => c.member_token === key);
      if (convo) cid = convo.conversation_id;
      else {
        try {
          const res = await api("membership-chat-open", { target_member_token: key });
          if (res.data?.error === 0) cid = res.data.conversation.conversation_id;
        } catch {
          /* skip this target */
        }
      }
      if (!cid) continue;
      for (const m of msgs) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await postMessage(m.body, { conversationId: cid });
        if (ok) sent += 1;
      }
    }
    setSelectMode(false);
    setSelectedIds(new Set());
    loadConversations();
    toast.info(sent ? `Forwarded to ${picks.length} ${picks.length === 1 ? "chat" : "chats"}.` : "Could not forward.");
  };

  /* --------------------------------------------------- initial + polling */
  useEffect(() => {
    ensureNotificationPermission();
    try {
      draftsRef.current = JSON.parse(lsGet(`chatdrafts:${token}`, "{}")) || {};
    } catch {
      draftsRef.current = {};
    }
    loadConversations();
    loadFavorites();
    loadBlocked();
    loadContacts("");
    const t = setInterval(loadConversations, 15000);
    return () => clearInterval(t);
  }, [loadConversations, loadFavorites, loadBlocked, loadContacts, token]);

  // Open the requested chat on mount AND whenever `startWith` changes to a new
  // member (e.g. clicking a second message notification while already here).
  useEffect(() => {
    if (!startWith || initedRef.current === startWith) return;
    initedRef.current = startWith;
    startChatWith({ member_token: startWith });
  }, [startWith, startChatWith]);

  /* ------------------------------------------------ realtime: open thread */
  useEffect(() => {
    if (!active?.conversation_id) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const channel = `conversation.${active.conversation_id}`;
    const ch = echo.private(channel);

    ch.listen(".message.sent", (payload) => {
      const msg = payload?.message;
      if (!msg || activeIdRef.current !== payload.conversation_id) return;
      setMessages((m) => (m.some((x) => x.id === msg.id) ? m : sortMsgs([...m, { ...msg, is_me: msg.sender_id === myId }])));
      if (msg.sender_id !== myId) {
        setOtherTyping(false);
        api("membership-chat-read", { conversation_id: payload.conversation_id }).catch(() => {});
        setConversations((list) =>
          list.map((c) =>
            c.conversation_id === payload.conversation_id
              ? { ...c, last_message: msg.body, last_message_at: msg.created_at, unread: 0 }
              : c
          )
        );
      }
    });

    ch.listen(".thread.event", (evt) => {
      if (!evt || activeIdRef.current !== evt.conversation_id) return;
      const d = evt.data || {};
      if (evt.kind === "read") {
        if (d.reader_id !== myId) setMessages((m) => m.map((x) => (x.is_me ? { ...x, read: true, delivered: true } : x)));
      } else if (evt.kind === "delivered") {
        if (d.by !== myId) setMessages((m) => m.map((x) => (x.is_me ? { ...x, delivered: true } : x)));
      } else if (evt.kind === "edit") {
        setMessages((m) => m.map((x) => (x.id === d.id ? { ...x, body: d.body, edited: true } : x)));
      } else if (evt.kind === "delete") {
        setMessages((m) => m.map((x) => (x.id === d.id ? { ...x, deleted: true, body: null, reactions: [] } : x)));
      } else if (evt.kind === "react") {
        setMessages((m) =>
          m.map((x) => {
            if (x.id !== d.id) return x;
            const mineMap = {};
            (x.reactions || []).forEach((r) => (mineMap[r.emoji] = r.mine));
            return { ...x, reactions: (d.reactions || []).map((r) => ({ ...r, mine: !!mineMap[r.emoji] })) };
          })
        );
      }
    });

    ch.listenForWhisper("typing", (e) => {
      if (e?.id === myId) return;
      setOtherTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setOtherTyping(false), 3500);
    });

    return () => {
      try {
        echo.leave(channel);
      } catch (e) {
        /* ignore */
      }
      clearTimeout(typingTimer.current);
      setOtherTyping(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.conversation_id, token, memberToken, myId]);

  /* ------------------------------------------------------------ presence */
  useEffect(() => {
    if (!communityId) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const name = `community.${communityId}`;
    const apply = (ids) => setOnlineIds(new Set(ids.map((u) => Number(u.id ?? u))));
    try {
      echo
        .join(name)
        .here((users) => apply(users))
        .joining((u) => setOnlineIds((s) => new Set(s).add(Number(u.id))))
        .leaving((u) =>
          setOnlineIds((s) => {
            const n = new Set(s);
            n.delete(Number(u.id));
            return n;
          })
        );
    } catch (e) {
      /* ignore */
    }
    return () => {
      try {
        echo.leave(name);
      } catch (e) {
        /* ignore */
      }
    };
  }, [communityId, token, memberToken]);

  /* --------------------------------------------------- fallback polling */
  useEffect(() => {
    if (!active?.conversation_id) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await api("membership-chat-messages", { conversation_id: active.conversation_id });
        if (cancelled || res.data?.error !== 0) return;
        const fresh = res.data.messages || [];
        setMessages((prev) => {
          const map = new Map(prev.map((m) => [m.id, m]));
          let changed = false;
          fresh.forEach((fm) => {
            const ex = map.get(fm.id);
            if (!ex) {
              map.set(fm.id, fm);
              changed = true;
            } else if (
              ex.read !== fm.read ||
              ex.delivered !== fm.delivered ||
              ex.edited !== fm.edited ||
              ex.deleted !== fm.deleted ||
              ex.body !== fm.body ||
              JSON.stringify(ex.reactions) !== JSON.stringify(fm.reactions)
            ) {
              map.set(fm.id, { ...ex, ...fm });
              changed = true;
            }
          });
          if (!changed) return prev;
          return sortMsgs(Array.from(map.values()));
        });
      } catch {
        /* ignore */
      }
    };
    const t = setInterval(refresh, 6000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.conversation_id, api]);

  /* ------------------------------------------------------------ scrolling */
  useEffect(() => {
    if (scrollRef.current && !loadingMore && stickRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, otherTyping, loadingMore]);

  const onScrollMessages = (e) => {
    const el = e.currentTarget;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    stickRef.current = bottom;
    setAtBottom(bottom);
    if (el.scrollTop < 40) loadOlder();
  };

  const jumpBottom = () => {
    stickRef.current = true;
    setAtBottom(true);
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  };

  const scrollToMessage = (id) => {
    const el = document.getElementById(`chatmsg-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /* --------------------------------------------------------- composer io */
  const onType = (e) => {
    const v = e.target.value;
    setText(v);
    if (active) {
      draftsRef.current[active.conversation_id] = v;
      if (!v) delete draftsRef.current[active.conversation_id];
      lsSet(`chatdrafts:${token}`, JSON.stringify(draftsRef.current));
    }
    if (!active || editing) return;
    const now = Date.now();
    if (now - lastWhisperRef.current > 1500) {
      lastWhisperRef.current = now;
      try {
        getEcho({ communityToken: token, memberToken })
          ?.private(`conversation.${active.conversation_id}`)
          .whisper("typing", { id: myId });
      } catch (err) {
        /* ignore */
      }
    }
  };

  const onPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith("image/"));
    if (!item) return;
    const file = item.getAsFile();
    if (file) {
      e.preventDefault();
      stageFile(file); // review + caption before it goes out
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = Array.from(e.dataTransfer?.files || []).find(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/") || f.type.startsWith("audio/")
    );
    if (file) stageFile(file);
  };

  /* ------------------------------------------------------------ helpers */
  const openContacts = () => {
    setShowContacts(true);
    setActive(null);
    setPreviewContact(null);
    setContactQuery("");
    // Reset the scope each time the picker opens — the member must choose a
    // group or a course before any contacts are shown. This call still fetches
    // both scope lists (contacts stay empty with no scope).
    setContactGroup("");
    setContactCourse("");
    setContacts([]);
    loadContacts("", "", "");
  };

  // Scope handlers for the new-message picker. The two scopes are exclusive —
  // picking one clears the other, so the contact list always has a single,
  // legible source.
  const selectContactGroup = (v) => {
    setContactGroup(v);
    setContactCourse("");
    loadContacts(contactQuery, v, "");
  };

  const selectContactCourse = (v) => {
    setContactCourse(v);
    setContactGroup("");
    loadContacts(contactQuery, "", v);
  };

  // Switching the Courses/Groups toggle drops whichever scope was active, so the
  // picker doesn't keep listing a group's members under a Courses heading.
  const selectContactMode = (m) => {
    setContactMode(m);
    setContactGroup("");
    setContactCourse("");
    setContacts([]);
  };

  // Landing shortcut: jump straight into the New-message picker pre-scoped to the
  // chosen group or course (DiscoverPane "empty" mode).
  const openScopedPicker = (key, mode = "courses") => {
    if (!key) return;
    setShowContacts(true);
    setActive(null);
    setPreviewContact(null);
    setContactQuery("");
    setContactMode(mode);
    if (mode === "courses") {
      setContactCourse(key);
      setContactGroup("");
      loadContacts("", "", key);
    } else {
      setContactGroup(key);
      setContactCourse("");
      loadContacts("", key, "");
    }
  };

  const startEdit = (m) => {
    setEditing({ id: m.id, body: m.body });
    setReplyTo(null);
    setText(m.body || "");
    setReactFor(null);
  };

  const startReply = (m) => {
    setEditing(null);
    setReplyTo({ id: m.id, is_me: m.is_me, preview: previewLabel(m.body) });
    setReactFor(null);
  };

  const copyMsg = (m) => {
    try {
      navigator.clipboard?.writeText(m.body || "");
      toast.info("Copied");
    } catch {
      /* ignore */
    }
    setReactFor(null);
  };

  const openSearchResult = (r) => {
    setConvoSearch("");
    setSearchResults(null);
    openConversation({
      conversation_id: r.conversation_id,
      name: r.name,
      img: r.img,
      member_token: r.member_token,
      other_id: r.other_id,
    });
    setTimeout(() => scrollToMessage(r.message_id), 350);
  };

  const threadImages = useMemo(() => messages.filter((m) => !m.deleted && isImageUrl(m.body)).map((m) => m.body), [messages]);
  const openImage = (url) => setLightbox({ index: Math.max(0, threadImages.indexOf(url)) });

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const pickWallpaper = (key) => {
    setWallpaper(key);
    if (active) lsSet(`chatwall:${token}:${active.conversation_id}`, key);
  };

  /* --------------------------------------------------- keyboard shortcuts */
  useEffect(() => {
    const onKey = (e) => {
      const typingInField = /^(INPUT|TEXTAREA)$/.test(e.target?.tagName || "");
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowContacts(false);
        searchBoxRef.current?.focus();
        return;
      }
      if (e.key === "Escape") {
        if (lightbox) return; // the lightbox handles its own Escape
        if (forwardMsgs) setForwardMsgs(null);
        else if (infoMsg) setInfoMsg(null);
        else if (reactFor) setReactFor(null);
        else if (findOpen) setFindOpen(false);
        else if (selectMode) setSelectMode(false);
        else if (detailsOpen) setDetailsOpen(false);
        else if (replyTo) setReplyTo(null);
        else if (editing) {
          setEditing(null);
          setText("");
        }
        return;
      }
      if (e.key === "ArrowUp" && !typingInField && active && !editing && !text) {
        const mine = [...messages].reverse().find((m) => m.is_me && !m.deleted && typeof m.id === "number");
        if (mine) {
          e.preventDefault();
          startEdit(mine);
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, forwardMsgs, infoMsg, reactFor, findOpen, selectMode, detailsOpen, replyTo, editing, active, messages, text]);

  /* ------------------------------------------------------------- render */
  const showListOnMobile = !active && !(showContacts && previewContact);
  const detailsDocked = detailsOpen && !!active;

  const inbox = (
    <InboxPane
      primary={primary}
      conversations={conversations}
      favorites={favorites}
      blocked={blocked}
      contacts={contacts}
      onlineIds={onlineIds}
      archivedCount={archivedCount}
      active={active}
      filter={filter}
      onFilter={(f) => {
        setFilter(f);
        setViewBlocked(false);
        setViewArchived(f === "archived");
      }}
      showContacts={showContacts}
      onOpenContacts={openContacts}
      onCloseContacts={() => {
        setShowContacts(false);
        setPreviewContact(null);
      }}
      contactQuery={contactQuery}
      onContactQuery={(v) => {
        setContactQuery(v);
        loadContacts(v, contactGroup, contactCourse);
      }}
      contactMode={contactMode}
      onSelectContactMode={selectContactMode}
      contactGroups={contactGroups}
      contactGroup={contactGroup}
      onSelectContactGroup={selectContactGroup}
      contactCourses={contactCourses}
      contactCourse={contactCourse}
      onSelectContactCourse={selectContactCourse}
      previewContact={previewContact}
      onPreviewContact={(c) => (compact ? startChatWith(c) : setPreviewContact(c))}
      convoSearch={convoSearch}
      onSearchChange={onSearchChange}
      searchResults={searchResults}
      onOpenSearchResult={openSearchResult}
      onOpenConversation={openConversation}
      onStartChat={startChatWith}
      onConvoAction={convoAction}
      onRowFavorite={toggleFavoriteFor}
      onUnblock={unblockMember}
      rowMenu={rowMenu}
      onRowMenu={setRowMenu}
      typingIn={otherTyping ? active?.conversation_id : null}
      viewBlocked={viewBlocked}
      onViewBlocked={setViewBlocked}
      embedded={embedded}
      onClose={onClose}
      searchRef={searchBoxRef}
    />
  );

  const shell = (
    <div
      className={`relative flex h-full w-full flex-col overflow-hidden bg-surface md:flex-row ${
        embedded
          ? // Phones get an edge-to-edge app surface (no card chrome); from sm up
            // it becomes a rounded panel inside the page container.
            "border-y border-slate-200/70 sm:rounded-2xl sm:border sm:shadow-sm"
          : "shadow-2xl ring-1 ring-black/5 sm:h-[86vh] sm:max-h-[760px] sm:w-[1120px] sm:max-w-[95vw] sm:rounded-[22px]"
      }`}
    >
      {/* ---------- Pane A: inbox ---------- */}
      <aside
        className={`${showListOnMobile ? "flex" : "hidden"} w-full min-h-0 min-w-0 flex-1 flex-col border-r border-slate-100 md:flex md:w-[288px] md:flex-none lg:w-[340px] xl:w-[368px]`}
        style={{ background: `linear-gradient(180deg, ${primary}14 0%, ${primary}08 22%, rgb(var(--c-surface-sunken)) 60%, rgb(var(--c-surface-sunken)) 100%)` }}
      >
        {inbox}
      </aside>

      {/* ---------- Pane B: thread / discovery ---------- */}
      <section className={`${showListOnMobile ? "hidden" : "flex"} min-h-0 min-w-0 flex-1 flex-col bg-surface md:flex`}>
        {active ? (
          <ThreadPane
            active={active}
            messages={messages}
            loadingMsgs={loadingMsgs}
            loadingMore={loadingMore}
            primary={primary}
            myId={myId}
            otherOnline={otherOnline}
            otherTyping={otherTyping}
            wallpaper={wallpaper}
            unreadAnchorId={unreadAnchorId}
            scrollRef={scrollRef}
            onScroll={onScrollMessages}
            atBottom={atBottom}
            unreadCount={activeConvo?.unread || 0}
            onJumpBottom={jumpBottom}
            onBack={() => setActive(null)}
            onClose={onClose}
            embedded={embedded}
            detailsOpen={detailsOpen}
            onToggleDetails={() => setDetailsOpen((o) => !o)}
            menuOpen={menuOpen}
            onMenuOpen={setMenuOpen}
            isFavorite={isFavorite}
            isMuted={isMuted}
            onToggleFavorite={toggleFavorite}
            onToggleMute={toggleMute}
            onBlock={blockMember}
            reactFor={reactFor}
            onMessageMenu={(id) => setReactFor(id)}
            onReact={toggleReaction}
            onReply={startReply}
            onEdit={startEdit}
            onDelete={deleteMsg}
            onCopy={copyMsg}
            onForward={(m) => {
              setForwardMsgs([m]);
              setForwardPicks(new Set());
            }}
            onSelectMode={(id) => {
              setSelectMode(true);
              setSelectedIds(new Set([id]));
            }}
            onInfo={setInfoMsg}
            onOpenImage={openImage}
            onJumpTo={scrollToMessage}
            onRetry={retryMessage}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onExitSelect={() => {
              setSelectMode(false);
              setSelectedIds(new Set());
            }}
            onBulkForward={() => {
              setForwardMsgs(messages.filter((m) => selectedIds.has(m.id)));
              setForwardPicks(new Set());
            }}
            selectedDeletable={deletableSelected.length}
            bulkBusy={bulkBusy}
            onSelectAll={() => setSelectedIds(new Set(messages.filter((m) => !m.deleted).map((m) => m.id)))}
            onBulkDelete={() => {
              // Nothing deletable in the selection is a real answer, not a
              // no-op: say so and leave the selection alone so it can be fixed.
              if (!deletableSelected.length) {
                toast.info("You can only delete messages you sent.");
                return;
              }
              setConfirmBulkDel(deletableSelected);
            }}
            findOpen={findOpen}
            onFindOpen={setFindOpen}
            dragging={dragging}
            onDrop={onDrop}
            onDragOver={(e) => {
              e.preventDefault();
              if (!dragging) setDragging(true);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget === e.target) setDragging(false);
            }}
            composer={{
              text,
              onType,
              onSend: send,
              sending,
              editing,
              onCancelEdit: () => {
                setEditing(null);
                setText("");
              },
              replyTo,
              onCancelReply: () => setReplyTo(null),
              onPickFile: stageFile,
              pendingAttach,
              onClearAttach: clearPending,
              uploading,
              onPickGif: (url) => url && postMessage(url),
              onPaste,
              onQuick: (s) => postMessage(s),
              primary,
              otherName: active.name,
            }}
          />
        ) : (
          <DiscoverPane
            mode={showContacts ? "contacts" : "empty"}
            primary={primary}
            member={member}
            contacts={contacts}
            conversations={conversations}
            onlineIds={onlineIds}
            previewContact={previewContact}
            onStartChat={startChatWith}
            onOpenContacts={openContacts}
            contactGroups={contactGroups}
            contactCourses={contactCourses}
            onScopeStart={openScopedPicker}
          />
        )}
      </section>

      {/* ---------- Pane C: details (docked on xl, drawer below) ---------- */}
      {detailsDocked && (
        <>
          <aside className="relative hidden w-[320px] shrink-0 xl:block">
            <DetailsPane
              docked
              active={active}
              messages={messages}
              primary={primary}
              online={otherOnline}
              isFavorite={isFavorite}
              isMuted={isMuted}
              wallpaper={wallpaper}
              onWallpaper={pickWallpaper}
              onToggleFavorite={toggleFavorite}
              onToggleMute={toggleMute}
              onBlock={blockMember}
              onReport={reportMember}
              onDeleteChat={() => convoAction(active.conversation_id, "delete", active.name)}
              onSearchInChat={() => {
                setDetailsOpen(false);
                setFindOpen(true);
              }}
              onOpenImage={openImage}
              onClose={() => setDetailsOpen(false)}
            />
          </aside>
          <div className="absolute inset-0 z-40 flex justify-end bg-ink-slate/30 backdrop-blur-[2px] xl:hidden" onClick={() => setDetailsOpen(false)}>
            <div className="chat-slide relative h-full w-full max-w-[340px] bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <DetailsPane
                active={active}
                messages={messages}
                primary={primary}
                online={otherOnline}
                isFavorite={isFavorite}
                isMuted={isMuted}
                wallpaper={wallpaper}
                onWallpaper={pickWallpaper}
                onToggleFavorite={toggleFavorite}
                onToggleMute={toggleMute}
                onBlock={blockMember}
                onReport={reportMember}
                onDeleteChat={() => convoAction(active.conversation_id, "delete", active.name)}
                onSearchInChat={() => {
                  setDetailsOpen(false);
                  setFindOpen(true);
                }}
                onOpenImage={openImage}
                onClose={() => setDetailsOpen(false)}
              />
            </div>
          </div>
        </>
      )}

      {/* ---------- forward sheet ---------- */}
      {forwardMsgs && (
        <div className="absolute inset-0 z-50 flex items-end justify-center bg-ink-slate/40 backdrop-blur-[2px] sm:items-center" onClick={() => setForwardMsgs(null)}>
          <div className="chat-pop flex max-h-[80%] w-full flex-col overflow-hidden rounded-t-2xl bg-surface shadow-2xl sm:m-4 sm:max-w-md sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: `${primary}14`, color: primary }}>
                <ForwardIcon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-extrabold text-slate-900">
                  Forward {forwardMsgs.length > 1 ? `${forwardMsgs.length} messages` : "message"}
                </p>
                <p className="truncate text-[11px] text-slate-400">{previewLabel(forwardMsgs[0]?.body)}</p>
              </div>
              <button type="button" onClick={() => setForwardMsgs(null)} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-2">
              {contacts.length === 0 ? (
                <p className="py-8 text-center text-[12px] text-slate-400">No members to forward to.</p>
              ) : (
                contacts.map((c) => {
                  const on = forwardPicks.has(c.member_token);
                  return (
                    <button
                      key={c.member_token}
                      type="button"
                      onClick={() =>
                        setForwardPicks((s) => {
                          const n = new Set(s);
                          if (n.has(c.member_token)) n.delete(c.member_token);
                          else n.add(c.member_token);
                          return n;
                        })
                      }
                      className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition hover:bg-slate-50"
                    >
                      <Avatar name={c.name} img={c.img} primary={primary} size={38} ring />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-slate-800">{c.name}</span>
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? "border-transparent text-white" : "border-slate-300 text-transparent"}`}
                        style={on ? { backgroundColor: primary } : undefined}
                      >
                        <CheckIcon className="h-3 w-3" width="3" />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t border-slate-100 p-3">
              <button
                type="button"
                onClick={doForward}
                disabled={!forwardPicks.size}
                className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[13px] font-extrabold text-white shadow-sm transition hover:brightness-110 disabled:opacity-40"
                style={brandGradient(primary)}
              >
                <ForwardIcon className="h-4 w-4" />
                Send{forwardPicks.size ? ` to ${forwardPicks.size}` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- message info ---------- */}
      {infoMsg && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-ink-slate/40 p-4 backdrop-blur-[2px]" onClick={() => setInfoMsg(null)}>
          <div className="chat-pop w-full max-w-xs overflow-hidden rounded-2xl bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-extrabold text-slate-900">Message info</p>
              <button type="button" onClick={() => setInfoMsg(null)} aria-label="Close" className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2.5 p-4 text-[12px]">
              <p className="line-clamp-3 rounded-xl bg-slate-50 px-3 py-2 text-slate-600">{previewLabel(infoMsg.body) || "—"}</p>
              {[
                ["Sent", fullTime(infoMsg.created_at), true],
                ["Delivered", infoMsg.delivered ? "Yes" : "Not yet", infoMsg.delivered],
                ["Read", infoMsg.read ? "Yes" : "Not yet", infoMsg.read],
                ...(infoMsg.edited ? [["Edited", "Yes", true]] : []),
              ].map(([k, v, ok]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-slate-400">{k}</span>
                  <span className={`font-bold ${ok ? "text-slate-700" : "text-slate-400"}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bulk-delete confirmation. A destructive action on several messages at
          once had no confirm at all before, and no report of what happened. */}
      {confirmBulkDel && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-ink-slate/45 p-4 backdrop-blur-[2px]" onClick={() => setConfirmBulkDel(null)}>
          <div className="chat-pop w-full max-w-[340px] overflow-hidden rounded-2xl bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pb-4 pt-5 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 dark:bg-rose-500/15 text-rose-500">
                <TrashIcon className="h-6 w-6" />
              </span>
              <p className="mt-3 text-[15px] font-extrabold text-slate-900">
                {confirmBulkDel.length === 1 ? "Delete this message?" : `Delete ${confirmBulkDel.length} messages?`}
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">
                {confirmBulkDel.length === 1 ? "It is" : "They are"} removed for everyone in this chat and replaced with{" "}
                <span className="font-semibold text-slate-700">“This message was deleted”</span>. This cannot be undone.
              </p>
              {selectedIds.size > confirmBulkDel.length && (
                <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-500">
                  {selectedIds.size - confirmBulkDel.length} of the {selectedIds.size} selected were sent by someone else and stay put.
                </p>
              )}
            </div>
            <div className="flex gap-2 border-t border-slate-100 p-3">
              <button
                type="button"
                onClick={() => setConfirmBulkDel(null)}
                className="flex-1 rounded-xl border border-slate-200 py-2.5 text-[13px] font-bold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runBulkDelete}
                className="flex-1 rounded-xl bg-rose-500 py-2.5 text-[13px] font-bold text-white shadow-sm transition hover:bg-rose-600 active:scale-[0.98]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-chat confirmation (WhatsApp-style) */}
      {confirmDel && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-ink-slate/45 p-4 backdrop-blur-[2px]" onClick={() => setConfirmDel(null)}>
          <div className="chat-pop w-full max-w-[320px] overflow-hidden rounded-2xl bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-4 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 dark:bg-rose-500/15 text-rose-500">
                <CloseIcon className="h-6 w-6" />
              </span>
              <p className="mt-3 text-[15px] font-extrabold text-slate-900">Delete chat?</p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">
                This clears your copy of the conversation with{" "}
                <span className="font-semibold text-slate-700">{confirmDel.name}</span>. They keep their messages, and you can chat again anytime.
              </p>
            </div>
            <div className="flex gap-2 border-t border-slate-100 p-3">
              <button
                type="button"
                onClick={() => setConfirmDel(null)}
                className="flex-1 rounded-xl border border-slate-200 py-2.5 text-[13px] font-bold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDeleteConvo}
                className="flex-1 rounded-xl bg-rose-500 py-2.5 text-[13px] font-bold text-white shadow-sm transition hover:bg-rose-600 active:scale-[0.98]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const withLightbox = (
    <>
      {shell}
      {lightbox && (
        <Lightbox
          images={threadImages}
          index={lightbox.index}
          onIndex={(i) => setLightbox({ index: i })}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );

  if (embedded) return withLightbox;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-ink-slate/50 backdrop-blur-sm sm:items-center sm:p-4">
      {withLightbox}
    </div>,
    document.body
  );
}
